import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import Loader from "../basics/Loader"
import { fetchPatchnotes } from "../../store/actions/patchnotesActions"

const APPS = [
	{ key: "tous", label: "Tous" },
	{ key: "svpapp", label: "SvpApp" },
	{ key: "svpmobile", label: "SvpMobile" },
	{ key: "svpcentral", label: "SvpCentral" },
]

const APP_ACCENT = {
	svpapp: COLORS.accent,
	svpmobile: COLORS.success,
	svpcentral: COLORS.warning,
}

const APP_LABEL = { svpapp: "SvpApp", svpmobile: "SvpMobile", svpcentral: "SvpCentral" }

const TYPE_CONFIG = {
	fix: { label: "Fix", bg: COLORS.dangerLight, color: COLORS.danger },
	feature: { label: "Nouveauté", bg: COLORS.successLight, color: COLORS.success },
}

function formatDate(iso) {
	if (!iso) return ""
	const [y, m, d] = iso.split("-")
	return `${d}.${m}.${y}`
}

const s = {
	content: { width: "100%", maxWidth: 720, margin: "0 auto" },
	empty: {
		padding: "60px 0 40px",
		textAlign: "center",
		color: COLORS.textLight,
		fontSize: 13,
	},
	card: (accent, hover) => ({
		backgroundColor: hover ? "#FAFAF9" : COLORS.surface,
		borderRadius: 12,
		border: `1px solid ${COLORS.border}`,
		borderLeft: `4px solid ${accent}`,
		marginBottom: 16,
		transition: "background-color 0.12s",
		overflow: "hidden",
	}),
	header: {
		padding: "16px 20px 0",
	},
	eyebrow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 6,
	},
	appLabel: (accent) => ({
		fontSize: 11,
		fontWeight: 600,
		color: accent,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
	}),
	date: {
		fontSize: 11,
		fontWeight: 500,
		color: COLORS.textLight,
		letterSpacing: "0.06em",
		fontVariantNumeric: "tabular-nums",
	},
	versionRow: {
		display: "flex",
		alignItems: "baseline",
		gap: 10,
	},
	version: {
		fontSize: 20,
		fontWeight: 700,
		color: COLORS.text,
		letterSpacing: "-0.02em",
	},
	titre: {
		fontSize: 13,
		color: COLORS.textMuted,
	},
	entries: {
		padding: "12px 20px 16px",
	},
	entry: {
		display: "flex",
		alignItems: "flex-start",
		padding: "5px 0",
	},
	badgeCol: {
		width: 76,
		flexShrink: 0,
		display: "flex",
		marginTop: 3,
	},
	typeBadge: (cfg) => ({
		fontSize: 10,
		fontWeight: 600,
		padding: "2px 8px",
		borderRadius: 99,
		background: cfg.bg,
		color: cfg.color,
		whiteSpace: "nowrap",
		letterSpacing: "0.02em",
		textTransform: "uppercase",
		lineHeight: "16px",
	}),
	texte: {
		fontSize: 13,
		lineHeight: 1.55,
		color: COLORS.text,
		paddingLeft: 6,
	},
	sep: {
		height: 1,
		backgroundColor: COLORS.border,
		margin: "0 20px",
		opacity: 0.6,
	},
}

function VersionCard({ p }) {
	const [hover, setHover] = useState(false)
	const accent = APP_ACCENT[p.app] || COLORS.textLight
	const entrees = p.entrees || []

	return (
		<div
			style={s.card(accent, hover)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			<div style={s.header}>
				<div style={s.eyebrow}>
					<span style={s.appLabel(accent)}>
						{APP_LABEL[p.app] || p.app}
					</span>
					<span style={s.date}>{formatDate(p.date)}</span>
				</div>
				<div style={s.versionRow}>
					<span style={s.version}>{p.version}</span>
					{p.titre && <span style={s.titre}>{p.titre}</span>}
				</div>
			</div>
			{entrees.length > 0 && <div style={s.sep} />}
			{entrees.length > 0 && (
				<div style={s.entries}>
					{entrees.map((e, i) => {
						const cfg = TYPE_CONFIG[e.type] || TYPE_CONFIG.fix
						return (
							<div key={i} style={s.entry}>
								<div style={s.badgeCol}>
									<span style={s.typeBadge(cfg)}>{cfg.label}</span>
								</div>
								<span style={s.texte}>{e.texte}</span>
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

export default function PatchnotesPage() {
	const dispatch = useDispatch()
	const { patchnotes, fetchPatchnotes: fetchState } = useSelector(
		(st) => st.patchnotesInfo,
	)
	const [filtre, setFiltre] = useState("tous")

	useEffect(() => {
		dispatch(fetchPatchnotes())
	}, [dispatch])

	const filtered =
		filtre === "tous"
			? patchnotes
			: patchnotes.filter((p) => p.app === filtre)

	return (
		<div style={gs.page}>
			<div style={s.content}>
				<div style={gs.header}>
					<h2 style={gs.title}>Patchnotes</h2>
				</div>

				<div style={gs.segmented}>
					{APPS.map((a) => (
						<button
							key={a.key}
							type="button"
							style={gs.segment(filtre === a.key)}
							onClick={() => setFiltre(a.key)}
						>
							{a.label}
						</button>
					))}
				</div>

				{fetchState.loading ? (
					<Loader
						variant="ring"
						message="Chargement des patchnotes…"
					/>
				) : filtered.length === 0 ? (
					<div style={s.empty}>
						{filtre === "tous"
							? "Aucun patchnote enregistré."
							: `Aucun patchnote pour ${APP_LABEL[filtre] || filtre}.`}
					</div>
				) : (
					filtered.map((p) => (
						<VersionCard
							key={`${p.app}-${p.version}`}
							p={p}
						/>
					))
				)}
			</div>
		</div>
	)
}
